/**
 * Selling Plan Selector Component
 *
 * Custom Element für die Auswahl von Selling Plans (Abonnements)
 * in Shopify Themes.
 *
 * @version 1.0.0
 */

(function() {
  'use strict';

  // ==========================================================================
  // LOGGING UTILITY
  // ==========================================================================

  const SHOW_LOGS = false;
  const LOG_PREFIX = '[selling_plan_picker]';
  const LOG_STYLES = {
    info: 'color: #2196F3; font-weight: bold;',
    success: 'color: #4CAF50; font-weight: bold;',
    warn: 'color: #FF9800; font-weight: bold;',
    error: 'color: #F44336; font-weight: bold;',
    debug: 'color: #9C27B0; font-weight: bold;'
  };

  const logger = {
    info: (message, data) => {
      if (!SHOW_LOGS) return;
      console.log(`%c${LOG_PREFIX} [INFO]`, LOG_STYLES.info, message, data !== undefined ? data : '');
    },
    success: (message, data) => {
      if (!SHOW_LOGS) return;
      console.log(`%c${LOG_PREFIX} [SUCCESS]`, LOG_STYLES.success, message, data !== undefined ? data : '');
    },
    warn: (message, data) => {
      if (!SHOW_LOGS) return;
      console.warn(`%c${LOG_PREFIX} [WARN]`, LOG_STYLES.warn, message, data !== undefined ? data : '');
    },
    error: (message, data) => {
      if (!SHOW_LOGS) return;
      console.error(`%c${LOG_PREFIX} [ERROR]`, LOG_STYLES.error, message, data !== undefined ? data : '');
    },
    debug: (message, data) => {
      if (!SHOW_LOGS) return;
      console.log(`%c${LOG_PREFIX} [DEBUG]`, LOG_STYLES.debug, message, data !== undefined ? data : '');
    },
    group: (label) => {
      if (!SHOW_LOGS) return;
      console.group(`${LOG_PREFIX} ${label}`);
    },
    groupEnd: () => {
      if (!SHOW_LOGS) return;
      console.groupEnd();
    },
    table: (data) => {
      if (!SHOW_LOGS) return;
      console.table(data);
    }
  };

  // ==========================================================================
  // SELLING PLAN SELECTOR CUSTOM ELEMENT
  // ==========================================================================

  if (!customElements.get('selling-plan-selector')) {

    class SellingPlanSelector extends HTMLElement {

      constructor() {
        super();

        // State
        this.buttons = null;
        this.intervalWrapper = null;
        this.intervalSelect = null;
        this.sellingPlanInput = null;
        this.form = null;
        this.initialized = false;
        this.isSubscription = false;

        // Config
        this.productId = null;
        this.formId = null;
        this.firstPlanId = null;
        this.priceData = null;
        this.variantsPriceData = null;
        this._pendingVariantId = null;

        logger.debug('Constructor aufgerufen');
      }

      // ========================================================================
      // LIFECYCLE
      // ========================================================================

      connectedCallback() {
        logger.info('connectedCallback - Element zum DOM hinzugefügt');

        // Warte auf nächsten Frame für DOM-Bereitschaft
        requestAnimationFrame(() => {
          this.init();
        });
      }

      disconnectedCallback() {
        logger.info('disconnectedCallback - Element vom DOM entfernt');
        this.cleanup();
      }

      // ========================================================================
      // INITIALIZATION
      // ========================================================================

      init() {
        if (this.initialized) {
          logger.warn('Bereits initialisiert - überspringe');
          return;
        }

        logger.group('Initialisierung');

        // Config aus data-Attributen lesen
        this.productId = this.dataset.productId;
        this.formId = this.dataset.formId;
        this.firstPlanId = this.dataset.firstPlanId;
        this.isSubscription = this.dataset.defaultSubscription === 'true';

        // Preisdaten laden
        try {
          this.priceData = JSON.parse(this.dataset.prices || '{}');
          logger.debug('Preisdaten geladen:', this.priceData);
        } catch (e) {
          logger.error('Fehler beim Parsen der Preisdaten:', e);
          this.priceData = {};
        }

        // Alle Varianten-Preisdaten laden (für korrekte Updates nach variant switch)
        try {
          this.variantsPriceData = JSON.parse(this.dataset.variantsPrices || '{}');
          logger.debug('Varianten-Preisdaten geladen:', {
            variantsCount: this.variantsPriceData ? Object.keys(this.variantsPriceData).length : 0
          });
        } catch (e) {
          logger.error('Fehler beim Parsen der Varianten-Preisdaten:', e);
          this.variantsPriceData = {};
        }

        logger.debug('Konfiguration geladen:', {
          productId: this.productId,
          formId: this.formId,
          firstPlanId: this.firstPlanId,
          defaultSubscription: this.isSubscription,
          hasPriceData: Object.keys(this.priceData).length > 0
        });

        // DOM Elemente finden
        this.buttons = this.querySelectorAll('.ds-selling-plan__btn');
        this.intervalWrapper = this.querySelector('[data-interval-wrapper]');
        this.intervalSelect = this.querySelector('[data-interval-select]');

        logger.debug('DOM Elemente gefunden:', {
          buttonsCount: this.buttons.length,
          hasIntervalWrapper: !!this.intervalWrapper,
          hasIntervalSelect: !!this.intervalSelect
        });

        if (this.buttons.length === 0) {
          logger.error('Keine Buttons gefunden! Initialisierung abgebrochen.');
          logger.groupEnd();
          return;
        }

        // Event Listeners binden
        this.bindEvents();

        // Form finden und konfigurieren
        this.findForm();

        // Variant/Section re-render Events
        this.bindVariantEvents();

        // Initialer Preis-Update (falls subscription_default true ist)
        if (this.isSubscription) {
          // Sofort updaten - unser Element hat keine Konflikte mit Drittanbieter-Scripts
          this.updatePrice();
        }

        this.initialized = true;
        logger.success('Initialisierung abgeschlossen');
        logger.groupEnd();
      }

      // ========================================================================
      // VARIANT EVENTS (theme.js)
      // ========================================================================

      bindVariantEvents() {
        if (!this.formId) return;

        const form = document.getElementById(this.formId) || document.forms[this.formId];
        if (!form) {
          logger.warn('bindVariantEvents: Form nicht gefunden', { formId: this.formId });
          return;
        }

        if (this._variantEventsBound) return;
        this._variantEventsBound = true;

        form.addEventListener('variant:change', (event) => {
          const variantId = event?.detail?.variant?.id;
          if (!variantId) return;

          logger.info('variant:change empfangen', { variantId });
          this._pendingVariantId = String(variantId);
          this.applyVariantPriceData(this._pendingVariantId);

          // Price-per-cup Elemente ausblenden (verhindert Flackern)
          document.querySelectorAll('.block-swatch__price-per-cup[data-variant-id]').forEach(el => {
            el.classList.add('is-updating');
          });
        });

        form.addEventListener('product:rerender', () => {
          // After re-render, price-list nodes are replaced. Re-apply current selection.
          logger.debug('product:rerender empfangen - re-apply price');

          if (this._pendingVariantId) {
            this.applyVariantPriceData(this._pendingVariantId);
          }
          this.updatePrice();

          // Price-per-cup Elemente einblenden nach Update
          requestAnimationFrame(() => {
            document.querySelectorAll('.block-swatch__price-per-cup[data-variant-id]').forEach(el => {
              el.classList.remove('is-updating');
            });
          });

          logger.debug('Preis nach rerender aktualisiert');
        });

      }

      applyVariantPriceData(variantId) {
        if (!variantId) return;
        if (!this.variantsPriceData || Object.keys(this.variantsPriceData).length === 0) return;

        const newData = this.variantsPriceData[String(variantId)];
        if (!newData) {
          logger.warn('Keine Varianten-Preisdaten für Variant gefunden', { variantId });
          return;
        }

        this.priceData = newData;
        this.dataset.prices = JSON.stringify(newData);
        logger.success('priceData auf Variant aktualisiert', { variantId });
      }

      // ========================================================================
      // FORM HANDLING
      // ========================================================================

      findForm() {
        logger.group('Form-Suche');

        this.form = document.getElementById(this.formId);

        if (!this.form) {
          logger.warn('Form nicht gefunden, versuche erneut in 100ms...', { formId: this.formId });
          setTimeout(() => this.findForm(), 100);
          logger.groupEnd();
          return;
        }

        logger.success('Form gefunden:', {
          formId: this.formId,
          formAction: this.form.action
        });

        // Alle Inputs im Form loggen
        const allInputs = this.form.querySelectorAll('input');
        logger.debug('Inputs im Form:', Array.from(allInputs).map(i => ({
          name: i.name,
          type: i.type,
          value: i.value
        })));

        // WICHTIG: Suche das selling_plan Input INNERHALB des Forms
        this.sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');

        if (this.sellingPlanInput) {
          logger.success('selling_plan Input im Form gefunden:', {
            id: this.sellingPlanInput.id,
            name: this.sellingPlanInput.name,
            value: this.sellingPlanInput.value,
            inForm: this.form.contains(this.sellingPlanInput)
          });
        } else {
          // Erstelle neues Input und füge es DIREKT nach dem id-Input ein
          logger.warn('selling_plan Input nicht gefunden - erstelle neues');
          this.sellingPlanInput = document.createElement('input');
          this.sellingPlanInput.type = 'hidden';
          this.sellingPlanInput.name = 'selling_plan';
          this.sellingPlanInput.id = `selling-plan-${this.formId}`;

          // Füge nach dem id-Input ein
          const idInput = this.form.querySelector('input[name="id"]');
          if (idInput && idInput.parentNode) {
            idInput.parentNode.insertBefore(this.sellingPlanInput, idInput.nextSibling);
          } else {
            this.form.insertBefore(this.sellingPlanInput, this.form.firstChild);
          }

          logger.success('selling_plan Input erstellt und eingefügt:', {
            id: this.sellingPlanInput.id,
            inForm: this.form.contains(this.sellingPlanInput)
          });
        }

        // Verifiziere dass Input wirklich im Form ist
        if (!this.form.contains(this.sellingPlanInput)) {
          logger.error('KRITISCH: selling_plan Input ist NICHT im Form!');
          // Versuche es erneut einzufügen
          this.form.insertBefore(this.sellingPlanInput, this.form.firstChild);
          logger.info('Input erneut eingefügt:', this.form.contains(this.sellingPlanInput));
        }

        // Initial State setzen
        this.updateSellingPlan(this.isSubscription);

        // Form Submit Handler - nutze CAPTURING um VOR allen anderen Handlern zu laufen
        this.form.addEventListener('submit', (e) => this.handleFormSubmit(e), { capture: true });

        // ZUSÄTZLICH: Höre auf das product-form Element
        const productForm = this.form.closest('product-form');
        if (productForm) {
          productForm.addEventListener('submit', (e) => this.handleFormSubmit(e), { capture: true });
          logger.debug('Submit Handler auch auf product-form registriert');
        }

        logger.groupEnd();
      }

      // ========================================================================
      // EVENT BINDING
      // ========================================================================

      bindEvents() {
        logger.group('Event Binding');

        // Button Click Events
        this.buttons.forEach((btn, index) => {
          btn.addEventListener('click', (e) => this.handleButtonClick(e));
          logger.debug(`Button ${index} Event Listener hinzugefügt:`, {
            option: btn.dataset.option,
            text: btn.textContent.trim().substring(0, 30)
          });
        });

        // Interval Select Event
        if (this.intervalSelect) {
          this.intervalSelect.addEventListener('change', (e) => this.handleIntervalChange(e));
          logger.debug('Interval Select Event Listener hinzugefügt');

          // Alle Optionen loggen
          const options = Array.from(this.intervalSelect.options).map(opt => ({
            value: opt.value,
            text: opt.text,
            selected: opt.selected
          }));
          logger.debug('Verfügbare Intervalle:', options);
        }

        logger.groupEnd();
      }

      // ========================================================================
      // EVENT HANDLERS
      // ========================================================================

      handleButtonClick(e) {
        try {
          // Nur preventDefault für diesen Button
          e.preventDefault();

          const clickedBtn = e.currentTarget;
          const previousState = this.isSubscription;
          this.isSubscription = clickedBtn.dataset.option === 'subscription';

          logger.group('Button Click');
          logger.info('Button geklickt:', {
            option: clickedBtn.dataset.option,
            previousState: previousState ? 'subscription' : 'one-time',
            newState: this.isSubscription ? 'subscription' : 'one-time'
          });

          // Button States aktualisieren
          this.buttons.forEach(btn => {
            btn.classList.remove('ds-selling-plan__btn--active');
          });
          clickedBtn.classList.add('ds-selling-plan__btn--active');
          logger.debug('Button CSS Klassen aktualisiert');

          // Interval Wrapper anzeigen/verstecken
          if (this.intervalWrapper) {
            if (this.isSubscription) {
              this.intervalWrapper.classList.remove('ds-selling-plan__interval--hidden');
              logger.debug('Interval Wrapper angezeigt');
            } else {
              this.intervalWrapper.classList.add('ds-selling-plan__interval--hidden');
              logger.debug('Interval Wrapper versteckt');
            }
          }

          // Selling Plan aktualisieren
          this.updateSellingPlan(this.isSubscription);

          // Preis sofort aktualisieren
          this.updatePrice();

          logger.groupEnd();
        } catch (error) {
          logger.error('Fehler in handleButtonClick:', error);
          console.error(error);
        }
      }

      handleIntervalChange(e) {
        logger.group('Interval Change');

        const newValue = e.target.value;
        const selectedText = e.target.options[e.target.selectedIndex].text;

        logger.info('Intervall geändert:', {
          value: newValue,
          text: selectedText
        });

        if (this.isSubscription && this.sellingPlanInput) {
          this.sellingPlanInput.value = newValue;
          logger.success('selling_plan Input aktualisiert:', newValue);

          // Preis sofort aktualisieren
          this.updatePrice();
        } else {
          logger.warn('Intervall geändert aber nicht im Subscription-Modus');
        }

        logger.groupEnd();
      }

      handleFormSubmit(e) {
        logger.group('Form Submit');

        logger.info('Form wird submitted...');
        logger.debug('isSubscription:', this.isSubscription);

        // KRITISCH: Finde das Input DIREKT im Form (nicht die gespeicherte Referenz)
        let inputInForm = this.form.querySelector('input[name="selling_plan"]');

        // Falls nicht vorhanden, erstelle es JETZT
        if (!inputInForm) {
          logger.warn('selling_plan Input nicht im Form - erstelle jetzt!');
          inputInForm = document.createElement('input');
          inputInForm.type = 'hidden';
          inputInForm.name = 'selling_plan';
          this.form.insertBefore(inputInForm, this.form.firstChild);
        }

        // Setze den Wert
        if (this.isSubscription) {
          const planId = this.intervalSelect ? this.intervalSelect.value : this.firstPlanId;
          inputInForm.value = planId;

          logger.success('ABO-KAUF - selling_plan gesetzt:', {
            planId: planId,
            inputName: inputInForm.name,
            inputValue: inputInForm.value,
            inDOM: this.form.contains(inputInForm)
          });
        } else {
          inputInForm.value = '';
          logger.info('EINZELKAUF - selling_plan Wert geleert');
        }

        // Aktualisiere auch die gespeicherte Referenz
        this.sellingPlanInput = inputInForm;

        // Alle Form-Daten loggen
        const formData = new FormData(this.form);
        const formDataObj = {};
        formData.forEach((value, key) => {
          formDataObj[key] = value;
        });

        logger.debug('FormData die gesendet wird:');
        logger.table(formDataObj);

        // Prüfen ob selling_plan in FormData ist
        if (formData.has('selling_plan')) {
          const planValue = formData.get('selling_plan');
          if (planValue) {
            logger.success('selling_plan ist in FormData enthalten:', planValue);
          } else {
            logger.info('selling_plan ist leer (Einzelkauf)');
          }
        } else {
          logger.error('FEHLER: selling_plan ist NICHT in FormData!');
        }

        logger.groupEnd();
      }

      // ========================================================================
      // STATE MANAGEMENT
      // ========================================================================

      updateSellingPlan(isSubscription) {
        logger.group('Update Selling Plan');

        if (!this.sellingPlanInput) {
          logger.error('sellingPlanInput ist null - kann nicht aktualisieren');
          logger.groupEnd();
          return;
        }

        // WICHTIG: name-Attribut IMMER setzen
        this.sellingPlanInput.setAttribute('name', 'selling_plan');

        if (isSubscription) {
          const planId = this.intervalSelect ? this.intervalSelect.value : this.firstPlanId;
          this.sellingPlanInput.value = planId;

          logger.success('Subscription-Modus aktiviert:', {
            planId: planId,
            inputValue: this.sellingPlanInput.value,
            inputName: this.sellingPlanInput.name
          });
        } else {
          this.sellingPlanInput.value = '';

          logger.info('Einzelkauf-Modus aktiviert:', {
            inputValue: this.sellingPlanInput.value,
            inputName: this.sellingPlanInput.name
          });
        }

        // Verifiziere das Input im DOM
        logger.debug('Input nach Update:', {
          element: this.sellingPlanInput,
          name: this.sellingPlanInput.getAttribute('name'),
          value: this.sellingPlanInput.value,
          inDOM: this.form.contains(this.sellingPlanInput)
        });

        logger.groupEnd();
      }


      // ========================================================================
      // PRICE UPDATE
      // ========================================================================

      updatePrice() {
        try {
          logger.group('Preis Update');

          if (!this.priceData || Object.keys(this.priceData).length === 0) {
            logger.warn('Keine Preisdaten verfügbar');
            logger.groupEnd();
            return;
          }

          // Preise aus den Daten holen
          const currencySymbol = this.priceData.currencySymbol || '€';
          const oneTimePrice = this.priceData.regularPrice;
          const realCompareAtPrice = this.priceData.compareAtPrice;
          const hasRealCompareAt = realCompareAtPrice && realCompareAtPrice > oneTimePrice;

          let newPrice, displayCompareAtPrice, discountPercent;

          if (this.isSubscription) {
            // === ABO MODUS ===
            const planId = this.intervalSelect ? this.intervalSelect.value : this.firstPlanId;
            const allocation = this.priceData.allocations?.[planId];

            if (allocation) {
              newPrice = allocation.price;

              // Höchsten Preis als Vergleichspreis nehmen
              if (hasRealCompareAt) {
                displayCompareAtPrice = realCompareAtPrice;
              } else {
                displayCompareAtPrice = oneTimePrice;
              }

              logger.debug('Abo-Preis verwendet:', { planId, newPrice, displayCompareAtPrice });
            } else {
              logger.warn('Keine Allocation für Plan ID gefunden:', planId);
              logger.groupEnd();
              return;
            }
          } else {
            // === EINMALKAUF MODUS ===
            newPrice = oneTimePrice;

            if (hasRealCompareAt) {
              displayCompareAtPrice = realCompareAtPrice;
            } else {
              displayCompareAtPrice = 0;
            }

            logger.debug('Einmalkauf-Preis verwendet:', { newPrice, displayCompareAtPrice });
          }

          // Prüfen ob Vergleichspreis angezeigt werden soll
          const shouldShowCompareAt = displayCompareAtPrice > newPrice;

          // Rabatt-Prozent berechnen
          if (shouldShowCompareAt && displayCompareAtPrice > 0) {
            discountPercent = Math.round(((displayCompareAtPrice - newPrice) / displayCompareAtPrice) * 100);
          } else {
            discountPercent = 0;
          }

          // Preise formatieren
          const formattedPrice = this.formatPrice(newPrice, currencySymbol);
          const formattedCompareAtPrice = this.formatPrice(displayCompareAtPrice, currencySymbol);

          // === DOM Updates ===

          // Hauptpreis
          const sellingPriceElement = document.querySelector('[data-selling-price]');
          if (sellingPriceElement) {
            sellingPriceElement.textContent = formattedPrice;

            if (shouldShowCompareAt) {
              sellingPriceElement.classList.add('selling-price-list__price--on-sale');
            } else {
              sellingPriceElement.classList.remove('selling-price-list__price--on-sale');
            }
          }

          // Vergleichspreis (durchgestrichen)
          const sellingComparePriceElement = document.querySelector('[data-selling-compare-price]');
          if (sellingComparePriceElement) {
            sellingComparePriceElement.textContent = formattedCompareAtPrice;

            if (shouldShowCompareAt) {
              sellingComparePriceElement.classList.remove('selling-price-list__compare--hidden');
            } else {
              sellingComparePriceElement.classList.add('selling-price-list__compare--hidden');
            }
          }

          // Prozent-Badge
          const sellingBadgeElement = document.querySelector('[data-selling-badge]');
          const discountPercentElement = document.querySelector('[data-selling-discount-percent]');
          const priceListElement = document.querySelector('[data-selling-price-list]');
          const showBadge = priceListElement?.dataset.showBadge === 'true';

          if (sellingBadgeElement && showBadge) {
            if (shouldShowCompareAt && discountPercent > 0) {
              sellingBadgeElement.classList.remove('selling-price-list__badge--hidden');
              if (discountPercentElement) {
                discountPercentElement.textContent = discountPercent;
              }
            } else {
              sellingBadgeElement.classList.add('selling-price-list__badge--hidden');
            }
          }

          logger.success('Preis aktualisiert:', {
            newPrice: formattedPrice,
            compareAtPrice: formattedCompareAtPrice,
            discountPercent: discountPercent + '%',
            isSubscription: this.isSubscription,
            showCompareAt: shouldShowCompareAt
          });

          // Button-Preis aktualisieren (ATC Button)
          this.updateButtonPrice(formattedPrice);

          // Einheitspreis aktualisieren
          this.updateUnitPrice(newPrice);

          // Preis pro Tasse aktualisieren
          this.updatePricePerCup();

          // Selector-Button-Preise aktualisieren
          this.updateSelectorPrices();

          logger.groupEnd();
        } catch (error) {
          logger.error('Fehler beim Preis-Update:', error);
          console.error(error);
        }
      }

      formatPrice(priceInCents, currencySymbol = '€') {
        if (priceInCents === null || priceInCents === undefined) {
          return `0,00${currencySymbol}`;
        }

        const euros = (priceInCents / 100).toFixed(2).replace('.', ',');
        return `${euros}${currencySymbol}`;
      }

      // ========================================================================
      // BUTTON PRICE UPDATE
      // ========================================================================

      updateButtonPrice(formattedPrice) {
        try {
          // Finde den ATC Button Preis über das data-atc-price Attribut
          const atcPriceElement = document.querySelector('[data-atc-price]');

          if (atcPriceElement) {
            atcPriceElement.textContent = formattedPrice;
            logger.success('Button-Preis aktualisiert:', formattedPrice);
          } else {
            logger.warn('ATC Button Preis-Element nicht gefunden (data-atc-price)');
          }
        } catch (error) {
          logger.error('Fehler beim Button-Preis Update:', error);
        }
      }

      // ========================================================================
      // UNIT PRICE UPDATE
      // ========================================================================

      updateUnitPrice(priceInCents) {
        try {
          logger.group('Einheitspreis Update');

          const unitPriceMeasurement = this.priceData?.unitPriceMeasurement;

          if (!unitPriceMeasurement || unitPriceMeasurement.quantityValue === 0) {
            logger.warn('Keine unitPriceMeasurement Daten verfügbar');
            logger.groupEnd();
            return;
          }

          const { referenceValue, referenceUnit, quantityValue, quantityUnit } = unitPriceMeasurement;
          const currencySymbol = this.priceData.currencySymbol || '€';

          logger.debug('unitPriceMeasurement:', { referenceValue, referenceUnit, quantityValue, quantityUnit });

          // Einheitspreis berechnen basierend auf Mengeneinheiten
          let factor = 1;

          // Konvertiere zu gleicher Einheit wenn nötig
          if (quantityUnit === 'g' && referenceUnit === 'kg') {
            // z.B. 250g Produkt, Referenz 1kg -> Faktor = 1000/250 = 4
            factor = (referenceValue * 1000) / quantityValue;
          } else if (quantityUnit === 'kg' && referenceUnit === 'kg') {
            // z.B. 1kg Produkt, Referenz 1kg -> Faktor = 1
            factor = referenceValue / quantityValue;
          } else if (quantityUnit === 'ml' && referenceUnit === 'l') {
            // z.B. 500ml Produkt, Referenz 1l -> Faktor = 1000/500 = 2
            factor = (referenceValue * 1000) / quantityValue;
          } else if (quantityUnit === 'l' && referenceUnit === 'l') {
            factor = referenceValue / quantityValue;
          } else if (quantityUnit === referenceUnit) {
            // Gleiche Einheit
            factor = referenceValue / quantityValue;
          } else {
            // Fallback: keine Konvertierung möglich
            logger.warn('Einheitenkonvertierung nicht unterstützt:', { quantityUnit, referenceUnit });
            factor = 1;
          }

          const unitPriceInCents = Math.round(priceInCents * factor);
          const formattedUnitPrice = this.formatPrice(unitPriceInCents, currencySymbol);

          logger.debug('Berechnung:', {
            priceInCents,
            factor,
            unitPriceInCents,
            formattedUnitPrice
          });

          // DOM Update
          const unitPriceElement = document.querySelector('[data-selling-unit-price]');

          if (unitPriceElement) {
            unitPriceElement.innerHTML = `<strong>${formattedUnitPrice}</strong>&nbsp;/ ${referenceValue} ${referenceUnit}`;
            logger.success('Einheitspreis aktualisiert:', `${formattedUnitPrice} / ${referenceValue} ${referenceUnit}`);
          } else {
            logger.warn('Einheitspreis-Element nicht gefunden (data-selling-unit-price)');
          }

          logger.groupEnd();
        } catch (error) {
          logger.error('Fehler beim Einheitspreis Update:', error);
          console.error(error);
        }
      }

      // ========================================================================
      // PRICE PER CUP UPDATE (ALL VARIANTS)
      // ========================================================================

      updatePricePerCup() {
        try {
          logger.group('Preis pro Tasse Update (alle Varianten)');

          const currencySymbol = this.priceData?.currencySymbol || '€';

          // Finde ALLE price-per-cup Elemente mit data-variant-id
          const allPricePerCupElements = document.querySelectorAll('.block-swatch__price-per-cup[data-variant-id]');

          if (allPricePerCupElements.length === 0) {
            logger.warn('Keine Preis-pro-Tasse-Elemente gefunden');
            logger.groupEnd();
            return;
          }

          logger.debug(`${allPricePerCupElements.length} Elemente gefunden`);

          // Aktuelles Selling Plan ID für Abo-Preise
          const currentPlanId = this.intervalSelect ? this.intervalSelect.value : this.firstPlanId;

          allPricePerCupElements.forEach(element => {
            const variantId = element.dataset.variantId;
            const weightInKg = parseFloat(element.dataset.weightKg);

            if (!variantId || !weightInKg || weightInKg <= 0) {
              logger.warn('Ungültige Daten für Element:', { variantId, weightInKg });
              return;
            }

            // Hole Preisdaten für diese Variante
            const variantData = this.variantsPriceData?.[variantId];

            if (!variantData) {
              logger.warn('Keine Preisdaten für Variante:', variantId);
              return;
            }

            // Bestimme den Preis basierend auf Abo/Einmalkauf
            let priceInCents;

            if (this.isSubscription) {
              // Abo-Preis: Hole allocation für den aktuellen Plan
              const allocation = variantData.allocations?.[currentPlanId];
              priceInCents = allocation?.price || variantData.regularPrice;
              logger.debug(`Variante ${variantId}: Abo-Preis = ${priceInCents}`);
            } else {
              // Einmalkauf-Preis
              priceInCents = variantData.regularPrice;
              logger.debug(`Variante ${variantId}: Einmalkauf-Preis = ${priceInCents}`);
            }

            // Kilopreis berechnen
            const kiloPriceInCents = priceInCents / weightInKg;

            // Preis pro Tasse = Kilopreis × 0,008 (8g pro Tasse)
            const pricePerCupInCents = kiloPriceInCents * 0.008;

            const formattedPricePerCup = this.formatPrice(Math.round(pricePerCupInCents), currencySymbol);

            // Element aktualisieren
            element.textContent = `${formattedPricePerCup} / Tasse`;

            logger.debug(`Variante ${variantId}: ${formattedPricePerCup} / Tasse (Gewicht: ${weightInKg}kg)`);
          });

          logger.success('Alle Tassenpreise aktualisiert');
          logger.groupEnd();
        } catch (error) {
          logger.error('Fehler beim Preis pro Tasse Update:', error);
          console.error(error);
        }
      }

      // ========================================================================
      // SELECTOR BUTTON PRICES UPDATE
      // ========================================================================

      updateSelectorPrices() {
        try {
          const currencySymbol = this.priceData?.currencySymbol || '€';
          const oneTimePrice = this.priceData?.regularPrice;
          const planId = this.intervalSelect ? this.intervalSelect.value : this.firstPlanId;
          const allocation = this.priceData?.allocations?.[planId];
          const subscriptionPrice = allocation?.price || oneTimePrice;

          // Abo-Preis Button
          const subscriptionPriceEl = this.querySelector('[data-subscription-price]');
          if (subscriptionPriceEl) {
            subscriptionPriceEl.textContent = this.formatPrice(subscriptionPrice, currencySymbol);
          }

          // Einmalkauf-Preis Button
          const onetimePriceEl = this.querySelector('[data-onetime-price]');
          if (onetimePriceEl) {
            onetimePriceEl.textContent = this.formatPrice(oneTimePrice, currencySymbol);
          }

          // Discount-Badge aktualisieren (Ersparnis Abo vs. Einmalkauf)
          const discountBadge = this.querySelector('[data-discount-badge]');
          const discountValueEl = this.querySelector('[data-discount-value]');

          if (discountBadge && oneTimePrice > subscriptionPrice) {
            const discountPercent = Math.round(((oneTimePrice - subscriptionPrice) / oneTimePrice) * 100);
            if (discountValueEl) {
              discountValueEl.textContent = discountPercent;
            }
            discountBadge.classList.remove('ds-selling-plan__badge--hidden');
          } else if (discountBadge) {
            discountBadge.classList.add('ds-selling-plan__badge--hidden');
          }

          logger.debug('Selector-Preise aktualisiert:', {
            subscription: subscriptionPrice,
            onetime: oneTimePrice
          });
        } catch (error) {
          logger.error('Fehler beim Selector-Preise Update:', error);
        }
      }

      // ========================================================================
      // PUBLIC API
      // ========================================================================

      getSellingPlan() {
        if (!this.isSubscription) {
          logger.debug('getSellingPlan() aufgerufen - Einzelkauf, return null');
          return null;
        }
        const planId = this.intervalSelect ? this.intervalSelect.value : this.firstPlanId;
        logger.debug('getSellingPlan() aufgerufen - return:', planId);
        return planId;
      }

      getState() {
        const state = {
          isSubscription: this.isSubscription,
          selectedPlanId: this.getSellingPlan(),
          formId: this.formId,
          productId: this.productId,
          initialized: this.initialized
        };
        logger.debug('getState() aufgerufen:', state);
        return state;
      }

      // ========================================================================
      // CLEANUP
      // ========================================================================

      cleanup() {
        logger.info('Cleanup durchgeführt');
        // Event Listeners werden automatisch entfernt wenn Element aus DOM entfernt wird
      }
    }

    // Element registrieren
    customElements.define('selling-plan-selector', SellingPlanSelector);
    logger.success('Custom Element "selling-plan-selector" registriert');

  } else {
    logger.warn('Custom Element "selling-plan-selector" bereits registriert');
  }

  // ==========================================================================
  // GLOBAL DEBUG HELPER
  // ==========================================================================

  // Debug-Funktion global verfügbar machen
  window.debugSellingPlanSelector = function() {
    const selectors = document.querySelectorAll('selling-plan-selector');
    logger.group('Debug Info');
    logger.info(`${selectors.length} selling-plan-selector Elemente gefunden`);

    selectors.forEach((selector, index) => {
      logger.info(`Element ${index}:`, selector.getState ? selector.getState() : 'nicht initialisiert');
    });

    logger.groupEnd();
  };

  logger.info('selling-plan-selector.js geladen');
  logger.info('Tipp: Rufe window.debugSellingPlanSelector() auf für Debug-Info');

  // ==========================================================================
  // NETWORK INTERCEPTOR - MODIFIZIERT SELLING PLAN VOR DEM REQUEST
  // ==========================================================================

  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    const [url, options] = args;

    // Prüfe ob es ein Cart-Add Request ist
    if (url && url.toString().includes('/cart/add')) {
      logger.group('NETWORK: Cart Add Request');
      logger.info('URL:', url);

      if (options && options.body && options.body instanceof FormData) {
        // Finde den aktiven Selling Plan Selector
        const selector = document.querySelector('selling-plan-selector');

        if (selector && selector.isSubscription) {
          const planId = selector.getSellingPlan();

          if (planId) {
            // SETZE DEN SELLING PLAN DIREKT IN DER FORMDATA
            logger.info('Setze selling_plan in FormData:', planId);
            options.body.set('selling_plan', planId);
          }
        }

        // Log FormData nach Modifikation
        const formDataObj = {};
        options.body.forEach((value, key) => {
          formDataObj[key] = value;
        });
        logger.debug('Request FormData (nach Modifikation):');
        logger.table(formDataObj);

        // Prüfe ob selling_plan enthalten ist
        if (options.body.has('selling_plan')) {
          const planValue = options.body.get('selling_plan');
          if (planValue) {
            logger.success('selling_plan wird gesendet:', planValue);
          } else {
            logger.info('selling_plan ist leer (Einzelkauf)');
          }
        } else {
          logger.warn('selling_plan ist NICHT im Request enthalten!');
        }
      }

      logger.groupEnd();
    }

    return originalFetch.apply(this, args);
  };

  logger.success('Network Interceptor aktiviert - modifiziert /cart/add.js Requests');

})();
